import type { IBetLimits, ILimit } from '../../../store/slices/bets/types';

export type ParseBetLimitsType = (result: IServerTableConfigResponseParams) => IBetLimits;
export type ParsePayoutsType = (result: IServerTableConfigResponseParams) => PayoutType[];

export interface ITableConfigParserOptions {
  parseBetLimits: ParseBetLimitsType;
  parsePayouts?: ParsePayoutsType;

  /** @deprecated This property is deprecated and will be removed in future versions. */
  overrideProperties?: Partial<TableConfigType>;
}

export interface PayoutType {
  code: number;
  payout: number;
}

export interface IServerAutoplayConfig {
  roundsCount?: number[];
  roundStepper?: number[];
  maxRounds?: number;
  remove?: boolean;
}

export enum WinMessageBranding {
  ONLY_PP_BRANDING = `onlyppbranding`,
  ONLY_TABLE_NAME = `onlytablename`,
  TOTAL_BRANDING = `totalbranding`,
  NONE = `none`,
}

export interface Jurisdiction {
  disableChat?: boolean;
  showTableLimitsPopup?: boolean;
}

export interface TableConfigType {
  // Language
  dedicatedLanguage: string | null;
  // Should be lobby button visible
  hideLobby: boolean;
  // When table was opened
  openTime: number;
  // Operator game ID
  operatorGameId: string;
  // Operator theme, used mostly only by lobby, at least now
  operatorTheme: string;
  // Maximum payout for table
  payoutBetMaxLimit?: number;
  // Used only in SnakesAndLadders
  euroTablePayoutMax?: number;
  // Bet limits for the table (min and max)
  tableBetLimit: ILimit;
  // Bet limits for the each bet spot (min and max)
  betLimits: IBetLimits;
  // Is table closed or not
  tableClosed: boolean;
  // Table name
  tableName: string;
  // Type of branding, that will be shown in win message (can be none)
  winMessageBranding: WinMessageBranding;
  // Jurisdiction represents compliance settings for table
  jurisdiction?: Jurisdiction;
  // Table variant represents "theme" of the table (usually different colors)
  tableVariant?: string;
  // Is infinite autoplay enabled
  isInfiniteAutoPlayEnabled?: boolean;
  // Should "crown" be shown in lobby button
  showPPBranding?: boolean;
  // Something that is used in NL Jurisdiction in crash game, not sure if it should stay
  defaultBetAmount?: number;
  // Used in help
  maxMultiplier?: number;
  // Used in payout table
  payouts: PayoutType[];
  // How many rounds can you stay idle before socket connection will be terminated
  idleLimit: number;
  // Flag to use new automated messages for game issue related events
  isAutomatedMessagesEnabled?: boolean;
  // Flag to show subtitles enabled for this table or not
  subtitlesAvailable: boolean;

  // Connection urls for WS and GS
  wsAddress: string;
  gsAddress: string;

  chatVisibleByDefault?: boolean;
  chatDisabled?: boolean;

  // TODO: To be moved into chipSlice
  chipColorCodes: string[];
  defaultChipOrder: number;
  chipAmounts: number[];
  betCode?: Record<string | number, string | number>;

  // NOT USED
  tableType: string;
  tableCategory?: string[];

  // Audio mode for voice over control
  audioMode: string | null;
}

export interface IServerTableConfigResponseParams {
  aamsSessionId: string | null;
  aamsTicket: string | null;
  betting_time: number;
  broadcaster_ws: string;
  chip_amounts: string;
  chip_color_codes: string | string[];
  default_chip_order: number;
  dedicatedLanguage: string | null;
  enable_video_stats: boolean;
  enable_av1_video: boolean;
  gamestats_address: string;
  gametype_id: number;
  gs_address: string;
  hls_location: string;
  hide_lobby: boolean;
  idle_limit_games: number;
  isPrizeDropBetDataSendEnabled: boolean;
  isPromoBetDataSendEnabled: boolean;
  openTime: number;
  open_hours: string;
  operator_theme: string;
  retrive_video_quality: boolean;
  session_launch_time_per_table: string | null;
  store_video_quality: boolean;
  table_bet_max_limit: number;
  table_bet_min_limit: number;
  subtitles_available?: boolean;
  subtitle?: string;
  table_category: string;
  table_closed: boolean;
  table_type: string;
  video_location: string;
  video_player_version: number;
  video_player_ios_version: number;
  ios_video_bandwidth_config: string;
  ios_abr_config: string;
  ws_address: string;
  youwinfeature: WinMessageBranding; // saved as winMessageBranding in store
  broadcaster_enabled: boolean;
  chatVisibleByDefault: boolean;
  isInfiniteAutoPlayEnabled: boolean;
  maxMultiplier: number;
  customMultiplierCap: number;
  canvasEnabled?: boolean;
  chatDisabled?: boolean;
  tableVariant?: string;
  hasNoCommissionToggle?: boolean;
  audioMode?: string | null;
  megaBaccaratMegaFeePercent?: number;
  disable_chinese_char?: string;
  disableSideBets?: string;
  operator_music?: string;
  bg_music?: boolean;
  autoplay?: IServerAutoplayConfig;
  automated_messages_enabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface IServerTableConfigResponse {
  betCode: Record<string | number, string | number>;
  operatorGameId: string;
  params: IServerTableConfigResponseParams;
  tableId: string;
  tableName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}
